/* eslint-disable jsx-a11y/click-events-have-key-events */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/**
 * @component TypeAhead
 * @author Shalini Jha
 * @desc Typeahead component is one that gives you suggestion for search results based on the
 * search input entered by the user. It makes an API call to fetch suggestion results.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { MaterialUiCore } from '@gscope-mfe/common-libs';
import styles from './TypeAhead.module.css';
import { Utilities } from '@gscope-mfe/app-bridge';
const { InputLabel } = MaterialUiCore;
const { CircularProgress } = MaterialUiCore;
const { Select } = MaterialUiCore;
const { MenuItem } = MaterialUiCore;
const { debounce } = Utilities;
const debouncedFn = debounce((callback) => {
    callback();
}, 500);
export const showSearchResults = (
    e,
    setSearchText,
    setShowSuggestion,
    characterOffset,
    fetchSearchResult,
    selectedSearchCat,
    setLoading,
    onChangeHandler,
) => {
    const searchInput = e.target.value;
    onChangeHandler(e.target.value);
    setSearchText(searchInput);
    setShowSuggestion(false);

    if (searchInput.length >= characterOffset && fetchSearchResult) {
        setShowSuggestion(true);
        setLoading(true);
        debouncedFn(() =>
            fetchSearchResult({
                searchText: searchInput,
                searchCategory: selectedSearchCat,
            }),
        );
    }

    return null;
};
export const getSelectedSuggestion = (
    item,
    suggestions,
    getSuggestionLabel,
    onChangeHandler,
    setSearchText,
    setShowSuggestion,
) => {
    const data = suggestions.filter((suggestion) => getSuggestionLabel(suggestion) === item);
    onChangeHandler(data[0]);
    setSearchText(item);
    setShowSuggestion(false);
    return data;
};
export const renderSearchSuggestions = (
    searchText,
    characterOffset,
    suggestions,
    getSuggestionLabel,
    onChangeHandler,
    setSearchText,
    setShowSuggestion,
) => {
    if (searchText.length < characterOffset) {
        return null;
    }

    if (suggestions.length == 0) {
        return <div className={styles.noRecord}>Search results empty</div>;
    }

    const formattedSuggestion = suggestions.slice(0, 101).map(getSuggestionLabel);
    return (
        <ul>
            {formattedSuggestion.map((item, index) => (
                <li
                    key={index}
                    onClick={(e) =>
                        getSelectedSuggestion(
                            item,
                            suggestions,
                            getSuggestionLabel,
                            onChangeHandler,
                            setSearchText,
                            setShowSuggestion,
                        )
                    }
                >
                    {item}
                </li>
            ))}
        </ul>
    );
};
export const resetSearch = (setSearchText, onChangeHandler) => {
    setSearchText('');
    onChangeHandler(null);
};
export const toggleShowSuggestion = (showSuggestion, setShowSuggestion) => setShowSuggestion(!showSuggestion);

const TypeAhead = ({
    labelContent,
    characterOffset,
    searchText,
    setSearchText,
    fetchSearchResult,
    searchCategory,
    suggestions,
    getSuggestionLabel,
    onChangeHandler,
    showSuggestion,
    setShowSuggestion,
    loading,
    setLoading,
    placeholder,
}) => {
    const [selectedSearchCat, setSelectedSearchCat] =
        searchCategory && searchCategory.length ? React.useState(searchCategory[0].id) : React.useState(null);
    return (
        <div className={styles.typeAhead}>
            <div className={styles.searchBarContainer}>
                {searchCategory && searchCategory.length ? (
                    <Select
                        id="selected-id"
                        className={styles.selectDropdown}
                        value={selectedSearchCat}
                        onChange={
                            /* istanbul ignore next */
                            (e) => setSelectedSearchCat(e.target.value)
                        }
                        inputProps={{
                            name: 'filter',
                            id: 'filter-select',
                        }}
                    >
                        {searchCategory.map(({ id, label }) => (
                            <MenuItem key={id} value={id}>
                                {label}
                            </MenuItem>
                        ))}
                    </Select>
                ) : null}
                <div className={styles.searchInput}>
                    <InputLabel className={styles.typeAheadLabel}>{labelContent}</InputLabel>
                    <input
                        type="text"
                        id="standard-search"
                        onChange={
                            /* istanbul ignore next */
                            (e) =>
                                showSearchResults(
                                    e,
                                    setSearchText,
                                    setShowSuggestion,
                                    characterOffset,
                                    fetchSearchResult,
                                    selectedSearchCat,
                                    setLoading,
                                    onChangeHandler,
                                )
                        }
                        value={searchText || ''}
                        placeholder={placeholder}
                    />
                </div>
                <div className={styles.inputAction}>
                    {searchText && searchText.length > 0 ? (
                        <span
                            className={`sct-icon icon-Remove-18 ${styles.removeIcon}`}
                            onClick={() => resetSearch(setSearchText, onChangeHandler)}
                        />
                    ) : null}
                    {
                        /* istanbul ignore next */
                        searchText && searchText.length > 0 && suggestions.length ? (
                            <span
                                className={`sct-icon ${showSuggestion ? 'icon-Caret-Up-18' : 'icon-Caret-Down-18'}`}
                                onClick={() => toggleShowSuggestion(showSuggestion, setShowSuggestion)}
                            />
                        ) : null
                    }
                </div>
            </div>
            {
                /* istanbul ignore next */
                searchText && searchText.length >= characterOffset && showSuggestion && (
                    <div className={styles.searchResults}>
                        {loading ? (
                            <div className={styles.loaderContainer}>
                                <CircularProgress color="secondary" size="3rem" className={styles.loader} />
                            </div>
                        ) : (
                            renderSearchSuggestions(
                                searchText,
                                characterOffset,
                                suggestions,
                                getSuggestionLabel,
                                onChangeHandler,
                                setSearchText,
                                setShowSuggestion,
                            )
                        )}
                    </div>
                )
            }
        </div>
    );
};

TypeAhead.propTypes = {
    labelContent: PropTypes.string.isRequired,
    characterOffset: PropTypes.number.isRequired,
    fetchSearchResult: PropTypes.func.isRequired,
    searchCategory: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        }),
    ),
    suggestions: PropTypes.array.isRequired,
    getSuggestionLabel: PropTypes.func.isRequired,
    onChangeHandler: PropTypes.func.isRequired,
    searchText: PropTypes.string,
    showSuggestion: PropTypes.bool.isRequired,
    setShowSuggestion: PropTypes.func.isRequired,
    setSearchText: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    setLoading: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
};
TypeAhead.defaultProps = {
    searchCategory: [],
};
export default TypeAhead;
